<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, onMounted, ref, watch } from "vue"
import { storeToRefs } from "pinia"
import { useI18n } from "vue-i18n"
import { useRoute, useRouter } from "vue-router"
import { useBuildingStore } from "@core/stores/building-v2.store"
import {
    getBuildingCadastreLocationRaw,
    toCadastreParcelFeature,
} from "@modules/pv/utils/buildingCadastreMapGeojson"
import type { PvSegment } from "./pv-form.types"

// ================================
// TYPES
// ================================
type GoogleSolarApi = {
    center?: { latitude?: number; longitude?: number }
    solarPotential?: {
        roofSegmentStats?: Array<{
            pitchDegrees?: number
            azimuthDegrees?: number
            stats?: { areaMeters2?: number }
            center?: { latitude?: number; longitude?: number }
        }>
    }
}

type MapSegment = {
    title: string
    content: { label: string; value: string }[]
    /**
     * Indice affiché sur la pastille (1-based), aligné sur le titre du pan.
     */
    index: number
    coordinates: [number, number]
    /** `true` = pastille bleue (pan actif en liste), `false` = gris. */
    isActive: boolean
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        idBuilding?: string
        listParamsPv?: PvSegment[] | null
        googleSolar?: Record<string, unknown> | null
        loading?: boolean
        noCadastralPlotSelected?: boolean
    }>(),
    {
        idBuilding: "",
        listParamsPv: null,
        googleSolar: null,
        loading: false,
        noCadastralPlotSelected: false,
    }
)

const emit = defineEmits<{
    (event: "click:previous"): void
    (event: "click:submit", payload: { listParamsPv: PvSegment[] }): void
}>()

// ================================
// I18N
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            pvDim: {
                roof: {
                    noParcel:
                        "Vous n'avez pas sélectionné la parcelle de votre bâtiment, nous ne pouvons donc pas récupérer les informations de Google Solar.",
                    selectParcel: "Sélectionner une parcelle",
                    googleError:
                        "Homeys a tenté de récupérer les informations des pans de toit grâce à Google Solar, mais aucune information n'a été trouvée.",
                    explanationTitle:
                        "Des résultats précis avec Google Solar et PVGIS",
                    explanationBody:
                        "Pour réaliser des analyses les plus fiables possibles, Homeys utilise les données de Google Solar et PVGis. Toutes ces données peuvent être ajustées ou modifiées si vous le souhaitez. Homeys récupère les informations des pans de toit grâce à Google Solar, libre à vous de les modifier. Nous récupérons les données de potentiel de production grâce à PVGis.",
                    titleGoogle: "Identifiés par Google Solar",
                    titleManual: "Pans de toit ajoutés manuellement",
                    removeGoogle: "Supprimer les informations de Google Solar",
                    restoreGoogle: "Récupérer les informations de Google Solar",
                    addRoof: "Ajouter un pan de toit",
                    back: "Retour",
                    launchStudy: "Lancer l'étude",
                    mapSurface: "Surface",
                    mapOrientation: "Orientation",
                    mapInclination: "Inclinaison",
                    cadastreParcel: "Parcelle cadastrale",
                },
                roofSegment: {
                    roof: "Pan de toit {n}",
                },
            },
        },
    },
})

// ================================
// STORES
// ================================
const buildingStore = useBuildingStore()
const { building } = storeToRefs(buildingStore)

// ================================
// ROUTER
// ================================
const router = useRouter()
const route = useRoute()

// ================================
// ETAT
// ================================
const googleRoofSegments = ref<PvSegment[]>([])
const finalGoogleRoofSegments = ref<PvSegment[]>([])
const customRoofSegments = ref<PvSegment[]>([])
// ================================
// HELPERS
// ================================
/**
 * Génère un identifiant unique pour un segment (aligné sur l’ex-legacy `createUniqueId`).
 * @returns identifiant court
 */
function createUniqueId(): string {
    return (Math.random().toString(36) + Date.now().toString(36)).slice(2)
}

/**
 * Crée un segment manuel vierge.
 * @param index - indice d’affichage
 * @param location - coordonnées ou null
 */
function createSegment(
    index: number,
    location: [number, number] | null
): PvSegment {
    return {
        id: `${createUniqueId()}-${index}`,
        peakpower: null,
        pvtechchoice: "crystSi",
        loss: 26,
        optimalangles: 1,
        angle: 0,
        aspect: 0,
        area: null,
        location,
        selected: true,
        index,
    }
}

/**
 * Réindexe les segments personnalisés.
 * @returns void
 */
function syncCustomIndices(): void {
    customRoofSegments.value = customRoofSegments.value.map(
        (segment, index) => ({
            ...segment,
            index,
        })
    )
}

/**
 * Réindexe les segments issus de Google.
 * @returns void
 */
function syncGoogleIndices(): void {
    googleRoofSegments.value = googleRoofSegments.value.map(
        (segment, index) => ({
            ...segment,
            index,
        })
    )
}

const getAddressLocation = computed((): [number, number] | null => {
    const data = props.googleSolar as GoogleSolarApi | null
    if (data?.center?.latitude != null && data?.center?.longitude != null) {
        return [data.center.latitude, data.center.longitude]
    }
    return null
})

const getMapSegments = computed((): MapSegment[] => {
    const segments: MapSegment[] = []
    googleRoofSegments.value?.forEach((item, index) => {
        const inFinal = finalGoogleRoofSegments.value.find(
            (element) => element.id === item.id
        )
        const isActive = inFinal != null && inFinal.selected !== false
        const newItem: MapSegment = {
            title: t("pvDim.roofSegment.roof", { n: index + 1 }),
            content: [
                {
                    label: t("pvDim.roof.mapSurface"),
                    value: `${item.area} m²`,
                },
                {
                    label: t("pvDim.roof.mapOrientation"),
                    value: `${item.aspect}°`,
                },
                {
                    label: t("pvDim.roof.mapInclination"),
                    value: `${item.angle}°`,
                },
            ],
            index: index + 1,
            coordinates: item.location?.[0]
                ? (item.location as [number, number])
                : (getAddressLocation.value as [number, number]),
            isActive,
        }
        segments.push(newItem)
    })
    return segments
})

const getErrorMessage = computed((): string | null => {
    if (
        !props.googleSolar &&
        !props.loading &&
        (props.listParamsPv == null || props.listParamsPv.length === 0)
    ) {
        return t("pvDim.roof.googleError")
    }
    return null
})

/**
 * Bloc d’aide Google + PVGis (création, données Solar disponibles).
 */
const getShowExplanation = computed((): boolean => {
    if (!props.googleSolar) {
        return false
    }
    if (props.listParamsPv && props.listParamsPv.length > 0) {
        return false
    }
    return true
})

const getMapReady = computed(() => getAddressLocation.value != null)

/**
 * Parcelle cadastrale du bâtiment courant pour surcouche verte sur la carte (aligné panneau segments).
 */
const getCadastreParcelMapFeature = computed(() =>
    toCadastreParcelFeature(
        getBuildingCadastreLocationRaw(building.value),
        t("pvDim.roof.cadastreParcel")
    )
)

const getCanSubmit = computed(() => {
    let isValid = true

    finalGoogleRoofSegments.value.forEach((element) => {
        if (element.selected === false) {
            return
        }
        const angleValid = element.angle >= 0 && element.angle <= 90
        const aspectValid = element.aspect >= -180 && element.aspect <= 180
        const isNotOptimalAnglesButValid = angleValid && aspectValid
        if (
            !(
                element.peakpower != null &&
                element.peakpower >= 0.1 &&
                element.peakpower <= 1000 &&
                element.loss != null &&
                element.loss >= 0 &&
                element.loss <= 100 &&
                (isNotOptimalAnglesButValid ||
                    Boolean(element.optimalangles)) &&
                Boolean(element.pvtechchoice)
            )
        ) {
            isValid = false
        }
    })

    customRoofSegments.value.forEach((element) => {
        const angleValid = element.angle >= 0 && element.angle <= 90
        const aspectValid = element.aspect >= -180 && element.aspect <= 180
        const isNotOptimalAnglesButValid = angleValid && aspectValid
        if (
            !(
                element.peakpower != null &&
                element.peakpower >= 0.1 &&
                element.peakpower <= 1000 &&
                element.loss != null &&
                element.loss >= 0 &&
                element.loss <= 100 &&
                (isNotOptimalAnglesButValid ||
                    Boolean(element.optimalangles)) &&
                Boolean(element.pvtechchoice)
            )
        ) {
            isValid = false
        }
    })

    const numberOfSelected = finalGoogleRoofSegments.value.filter(
        (el) => el.selected
    ).length
    if (
        (numberOfSelected === 0 || numberOfSelected === undefined) &&
        (customRoofSegments.value == null ||
            customRoofSegments.value.length === 0)
    ) {
        isValid = false
    }

    return isValid
})

// ================================
// LOGIQUE SEGMENTS
// ================================
/**
 * Construit les listes de segments (édition, création + Google, ou manuel seul).
 * @returns void
 */
function buildSegmentsFromProps(): void {
    googleRoofSegments.value = []
    finalGoogleRoofSegments.value = []
    customRoofSegments.value = []
    if (props.listParamsPv != null && props.listParamsPv.length > 0) {
        props.listParamsPv.forEach((item) => {
            if (item.location) {
                googleRoofSegments.value.push({ ...item })
            } else {
                customRoofSegments.value.push({ ...item })
            }
        })
        syncGoogleIndices()
        customRoofSegments.value = customRoofSegments.value.map(
            (el, index) => ({
                ...el,
                index,
            })
        )
        finalGoogleRoofSegments.value = [...googleRoofSegments.value]
        return
    }

    if (props.googleSolar) {
        const data = props.googleSolar as GoogleSolarApi
        const stats = data.solarPotential?.roofSegmentStats
        if (Array.isArray(stats) && stats.length > 0) {
            stats.forEach((element, index) => {
                const pitch = Number(element.pitchDegrees?.toFixed(2) ?? 0)
                const azimuth = Number(
                    (Number(element.azimuthDegrees) - 180).toFixed(2)
                )
                const newItem: PvSegment = {
                    peakpower: null,
                    pvtechchoice: "crystSi",
                    loss: 26,
                    optimalangles: 0,
                    angle: pitch,
                    aspect: azimuth,
                    area: element.stats?.areaMeters2
                        ? Number(element.stats.areaMeters2.toFixed(2))
                        : null,
                    location:
                        element.center?.latitude != null &&
                        element.center?.longitude != null
                            ? [
                                  element.center.latitude,
                                  element.center.longitude,
                              ]
                            : null,
                    selected: true,
                    id: `${Math.round(
                        Number(element.azimuthDegrees)
                    )}_${Math.round(Number(element.pitchDegrees))}_${createUniqueId()}`,
                    index,
                }
                googleRoofSegments.value.push(newItem)
            })
        }
        finalGoogleRoofSegments.value = [...googleRoofSegments.value]
    }

    if (
        googleRoofSegments.value.length === 0 &&
        (props.listParamsPv == null || props.listParamsPv.length === 0) &&
        !props.googleSolar
    ) {
        customRoofSegments.value = [createSegment(0, null)]
    }
    if (
        googleRoofSegments.value.length === 0 &&
        props.googleSolar &&
        !customRoofSegments.value.length
    ) {
        const stats = (props.googleSolar as GoogleSolarApi).solarPotential
            ?.roofSegmentStats
        if (!Array.isArray(stats) || stats.length === 0) {
            customRoofSegments.value = [createSegment(0, null)]
        }
    }
}

watch(
    () => [props.listParamsPv, props.googleSolar, props.loading] as const,
    () => {
        buildSegmentsFromProps()
    },
    { deep: true }
)

onMounted(() => {
    buildSegmentsFromProps()
})

function onClickAddSegment(): void {
    const nextIndex = customRoofSegments.value.length
    customRoofSegments.value.push({
        ...createSegment(nextIndex, null),
        isNew: true,
    })
    syncCustomIndices()
}

function onClickDeleteSegment(data: PvSegment): void {
    customRoofSegments.value = customRoofSegments.value.filter(
        (s) => s.id !== data.id
    )
    syncCustomIndices()
}

function onSubmitGoogleSegment(data: PvSegment): void {
    finalGoogleRoofSegments.value = finalGoogleRoofSegments.value.map((s) =>
        s.id === data.id ? data : s
    )
}

function onSubmitCustomSegment(data: PvSegment): void {
    customRoofSegments.value = customRoofSegments.value.map((s) =>
        s.id === data.id ? data : s
    )
}

function onSubmitNewCustomSegment(data: PvSegment): void {
    const { isNew: _isNew, ...rest } = data
    const next: PvSegment = {
        ...rest,
        id: `${Math.round(data.aspect)}_${Math.round(
            data.angle
        )}_${createUniqueId()}`,
    }
    customRoofSegments.value = customRoofSegments.value.map((s, sIndex) =>
        sIndex === data.index ? next : s
    )
}

function onClickReturn(): void {
    emit("click:previous")
}

function onClickSubmit(): void {
    finalGoogleRoofSegments.value = finalGoogleRoofSegments.value.filter(
        (e) => e.selected !== false
    )
    const list = [...finalGoogleRoofSegments.value, ...customRoofSegments.value]
    list.forEach((element, index) => {
        element.index = index
        element.id = `${Math.round(element.aspect)}_${Math.round(
            element.angle
        )}_${createUniqueId()}`
    })
    emit("click:submit", { listParamsPv: list })
}

function onHideGoogleSolar(): void {
    finalGoogleRoofSegments.value = []
}

function onRestoreGoogleSolar(): void {
    finalGoogleRoofSegments.value = [...googleRoofSegments.value]
}

function onClickSelectCadastralPlot(): void {
    const idBuilding = String(
        route.params._id_building || props.idBuilding || ""
    )
    if (!idBuilding) {
        return
    }
    const path = `/building/${idBuilding}/info-general`
    if (typeof window !== "undefined" && window.parent !== window) {
        window.parent.postMessage(
            {
                type: "navigate",
                path,
            },
            "*"
        )
        return
    }
    void router.push(path)
}
</script>

<template>
    <div class="pv-form-roof flex w-full flex-col gap-4">
        <div v-if="props.loading" class="w-full">
            <BaseSkeleton height="32rem" class="w-full" />
        </div>

        <template v-else>
            <BaseAppMessage
                v-if="props.noCadastralPlotSelected"
                severity="warn"
                class="w-full"
                :closable="false"
            >
                <div
                    class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                >
                    <span>
                        {{ t("pvDim.roof.noParcel") }}
                    </span>
                    <BaseButton
                        outlined
                        type="button"
                        size="small"
                        :label="t('pvDim.roof.selectParcel')"
                        @click="onClickSelectCadastralPlot"
                    />
                </div>
            </BaseAppMessage>

            <div v-else>
                <BaseAppMessage
                    v-if="getErrorMessage"
                    severity="warn"
                    class="w-full"
                    :message="getErrorMessage"
                    :closable="false"
                />

                <PVRoofMap
                    v-if="getMapReady"
                    :address-location="getAddressLocation"
                    :segments="getMapSegments"
                    :cadastre-feature="getCadastreParcelMapFeature"
                />

                <BaseAppMessage
                    v-if="getShowExplanation"
                    severity="info"
                    class="mt-4 w-full"
                    :closable="true"
                >
                    <template #header>
                        <span class="text-sm">
                            {{ t("pvDim.roof.explanationTitle") }}
                        </span>
                    </template>
                    <p class="text-sm leading-relaxed text-surface-600">
                        {{ t("pvDim.roof.explanationBody") }}
                    </p>
                </BaseAppMessage>
            </div>
        </template>

        <div
            v-if="googleRoofSegments.length > 0"
            class="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
        >
            <h2 class="text-base font-medium text-primary">
                {{ t("pvDim.roof.titleGoogle") }}
            </h2>
            <div
                v-if="!props.listParamsPv?.length"
                class="flex flex-wrap items-center justify-end gap-2"
            >
                <BaseButton
                    v-if="finalGoogleRoofSegments.length > 0"
                    type="button"
                    outlined
                    size="small"
                    icon="pi pi-trash"
                    :label="t('pvDim.roof.removeGoogle')"
                    @click="onHideGoogleSolar"
                />
                <BaseButton
                    v-if="
                        googleRoofSegments.length > 0 &&
                        finalGoogleRoofSegments.length === 0
                    "
                    type="button"
                    outlined
                    size="small"
                    icon="pi pi-refresh"
                    :label="t('pvDim.roof.restoreGoogle')"
                    @click="onRestoreGoogleSolar"
                />
            </div>
        </div>

        <ul v-if="finalGoogleRoofSegments.length > 0" class="flex flex-col">
            <li
                v-for="item in finalGoogleRoofSegments"
                :key="item.id"
                class="list-none border-b border-surface-200"
            >
                <PVFormRoofSegment
                    v-if="item.location"
                    :data="item"
                    :can-disable="true"
                    :is-new="item.isNew === true"
                    @submit:modification="onSubmitGoogleSegment"
                />
            </li>
        </ul>

        <h2 class="text-base font-medium text-primary">
            {{ t("pvDim.roof.titleManual") }}
        </h2>

        <ul class="flex flex-col">
            <li
                v-for="item in customRoofSegments"
                :key="item.id"
                class="list-none border-b border-surface-200"
            >
                <PVFormRoofSegment
                    :data="item"
                    :can-delete="true"
                    :is-new="item.isNew === true"
                    @delete="onClickDeleteSegment"
                    @submit:modification="onSubmitCustomSegment"
                    @submit:new="onSubmitNewCustomSegment"
                />
            </li>
        </ul>

        <div class="mt-4">
            <BaseButton
                type="button"
                text
                icon="pi pi-plus"
                :label="t('pvDim.roof.addRoof')"
                @click="onClickAddSegment"
            />
        </div>

        <footer
            class="mt-6 flex w-full flex-wrap items-center justify-between gap-3 border-t border-surface-200 pt-4"
        >
            <BaseButton
                type="button"
                outlined
                :label="t('pvDim.roof.back')"
                @click="onClickReturn"
            />
            <BaseButton
                type="button"
                :label="t('pvDim.roof.launchStudy')"
                :disabled="!getCanSubmit"
                @click="onClickSubmit"
            />
        </footer>
    </div>
</template>
